import React from 'react';
import PropTypes from 'prop-types';

const ReportsFilters = ({
  filters,
  onFilterChange,
  onClearFilters,
  reportSummary,
  year
}) => {
  const handleFilterChange = (key, value) => {
    onFilterChange({ [key]: value });
  };

  const hasActiveFilters =
    filters.courthouse ||
    filters.judge ||
    filters.prosecutor ||
    filters.chargeCode ||
    filters.minFrequency > 1;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Filter Charge Reports
          </h3>
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="text-sm text-blue-600 hover:text-blue-500 font-medium"
            >
              Clear All Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Courthouse Filter */}
          <div>
            <label
              htmlFor="courthouse"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Courthouse
            </label>
            <select
              id="courthouse"
              value={filters.courthouse}
              onChange={(e) => handleFilterChange('courthouse', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">All Courthouses</option>
              {reportSummary &&
                Object.keys(reportSummary.courthouseStats)
                  .sort()
                  .map((courthouse) => (
                    <option key={courthouse} value={courthouse}>
                      {courthouse} ({reportSummary.courthouseStats[courthouse]})
                    </option>
                  ))}
            </select>
          </div>

          {/* Judge Filter */}
          <div>
            <label
              htmlFor="judge"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Judge
            </label>
            <select
              id="judge"
              value={filters.judge}
              onChange={(e) => handleFilterChange('judge', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">All Judges</option>
              {reportSummary &&
                Object.keys(reportSummary.judgeStats)
                  .sort()
                  .map((judge) => (
                    <option key={judge} value={judge}>
                      {judge} ({reportSummary.judgeStats[judge]})
                    </option>
                  ))}
            </select>
          </div>

          {/* Prosecutor Filter */}
          <div>
            <label
              htmlFor="prosecutor"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Prosecutor
            </label>
            <select
              id="prosecutor"
              value={filters.prosecutor}
              onChange={(e) => handleFilterChange('prosecutor', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">All Prosecutors</option>
              {reportSummary &&
                Object.keys(reportSummary.prosecutorStats)
                  .sort()
                  .map((prosecutor) => (
                    <option key={prosecutor} value={prosecutor}>
                      {prosecutor} ({reportSummary.prosecutorStats[prosecutor]})
                    </option>
                  ))}
            </select>
          </div>

          {/* Charge Code Filter */}
          <div>
            <label
              htmlFor="chargeCode"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Charge Code
            </label>
            <input
              type="text"
              id="chargeCode"
              value={filters.chargeCode}
              onChange={(e) => handleFilterChange('chargeCode', e.target.value)}
              placeholder="e.g., PC187, VC23152"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* Minimum Frequency Filter */}
          <div>
            <label
              htmlFor="minFrequency"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Min Frequency
            </label>
            <input
              type="number"
              id="minFrequency"
              value={filters.minFrequency}
              onChange={(e) =>
                handleFilterChange(
                  'minFrequency',
                  parseInt(e.target.value) || 1
                )
              }
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {filters.courthouse && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Courthouse: {filters.courthouse}
                  <button
                    onClick={() => handleFilterChange('courthouse', '')}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500"
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      className="w-2 h-2"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 8 8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeWidth="1.5"
                        d="m1 1 6 6m0-6-6 6"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {filters.judge && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Judge: {filters.judge}
                  <button
                    onClick={() => handleFilterChange('judge', '')}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500"
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      className="w-2 h-2"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 8 8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeWidth="1.5"
                        d="m1 1 6 6m0-6-6 6"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {filters.prosecutor && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Prosecutor: {filters.prosecutor}
                  <button
                    onClick={() => handleFilterChange('prosecutor', '')}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500"
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      className="w-2 h-2"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 8 8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeWidth="1.5"
                        d="m1 1 6 6m0-6-6 6"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {filters.chargeCode && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Charge: {filters.chargeCode}
                  <button
                    onClick={() => handleFilterChange('chargeCode', '')}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-yellow-400 hover:bg-yellow-200 hover:text-yellow-500"
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      className="w-2 h-2"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 8 8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeWidth="1.5"
                        d="m1 1 6 6m0-6-6 6"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {filters.minFrequency > 1 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Min: {filters.minFrequency}
                  <button
                    onClick={() => handleFilterChange('minFrequency', 1)}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500"
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      className="w-2 h-2"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 8 8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeWidth="1.5"
                        d="m1 1 6 6m0-6-6 6"
                      />
                    </svg>
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ReportsFilters.propTypes = {
  filters: PropTypes.shape({
    courthouse: PropTypes.string,
    judge: PropTypes.string,
    prosecutor: PropTypes.string,
    chargeCode: PropTypes.string,
    minFrequency: PropTypes.number
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  reportSummary: PropTypes.shape({
    courthouseStats: PropTypes.array,
    judgeStats: PropTypes.array,
    prosecutorStats: PropTypes.array
  }),
  year: PropTypes.string.isRequired
};

export default ReportsFilters;
